<template>
  <custom-callout ref="row-ref" :has-toggle="false" :has-border="false">
    <validation-observer ref="signatureForm" novalidate>
      <div class="row">
        <div class="col-12 col-md-4 my-2 row">
          <div class="col-12 col-md-2 d-flex align-items-center">
            <icon-view
              icon="fas fa-user-edit"
              icon-color="#ff7058"
              iconBackground="#ffffff"
              icon-size="19px;"
              variant="custom-icon"
            />
          </div>
          <div class="col-12 col-md-10">
            <span class="data-title d-block border-mobile">Puesto:</span>
            <span class="font-16 color-black">
              <validation-provider
                mode="eager"
                rules="required"
                v-slot="{ errors }"
              >
                <CustomDropDown
                  id="ddlSelectedPosition"
                  v-model="info.selectedPosition"
                  :options="positions"
                  fieldkey="id"
                  fieldname="name"
                  placeholder="Seleccione"
                  :error="errors[0]"
                  custom-error-msg="Favor seleccionar puesto"
                  :disabled="isPositionReadOnly || isDisabled"
                  placement-top
                />
              </validation-provider>
            </span>
          </div>
        </div>
        <div class="col-12 col-md-3 my-2">
          <span class="data-title d-block">Nombre:</span>
          <span class="font-16 color-black">
            <validation-provider v-slot="{ errors }" rules="required">
              <CustomInput
                id="txtName"
                v-model="info.name"
                class="custom-input-signature"
                required
                :error="errors[0]"
                custom-error-msg="El nombre es requerido."
                :disabled="isNameReadOnly || isDisabled"
              />
            </validation-provider>
          </span>
        </div>
        <div class="col-12 col-md-2 my-2">
          <span class="data-title d-block">Tipo de Firma:</span>
          <span class="font-16 color-black">
            <validation-provider
              mode="eager"
              rules="required"
              v-slot="{ errors }"
            >
              <CustomDropDown
                id="ddlSelectedSignatureType"
                v-model="info.selectedSignatureType"
                :options="signatureTypes"
                fieldkey="id"
                fieldname="name"
                :error="errors[0]"
                custom-error-msg="Favor seleccionar tipo de firma"
                placeholder="Seleccione"
                :disabled="isDisabled || isReadOnlySignatureType"
                placement-top
              />
            </validation-provider>
          </span>
        </div>
        <div v-if="showEmailInput" class="col-12 col-md-2 my-2">
          <span class="data-title d-block">Correo Electrónico:</span>
          <span class="font-16 color-black">
            <validation-provider
              name="email"
              rules="required|email"
              v-slot="{ errors }"
              :custom-messages="{
                required: 'El correo electrónico es requerido.',
                email: 'Favor de entrar un correo electrónico válido.'
              }"
            >
              <CustomInput
                id="signaturePanelEmail"
                v-model="info.email"
                :error="errors[0]"
                :disabled="isDisabled"
                :custom-error-msg="errors[0]"
                type="email"
              />
            </validation-provider>
          </span>
        </div>
        <div
          v-if="showGoSignButton && isDigitalSignatureOptionSelected"
          class="col-12 col-md-2 my-2 mt-4 d-flex align-items-start"
        >
          <action-button
            id="btn-sign"
            i="fas fa-signature"
            text="Firmar"
            variant="success mt-2"
            :disabled="isDisabled"
            @click="goSign"
          />
        </div>
      </div>
      <div
        v-show="isDigitalSignatureOptionSelected && signatureModel"
        class="container-fluid row my-3 rounded"
      >
        <topaz
          id="topazSignature"
          ref="topaz"
          mainContainerClass="border"
          imgClass="my-3"
          @save-signature="saveSignature"
          @reset-signature="resetSignature"
          @is-signing="handleWaiting"
          @user-canceled="handleUserCancelled"
        />
      </div>
      <div class="dropdown-divider my-3"></div>
      <div class="row">
        <span
          class="col-12 font-weight-bold font-18 color-black data-title d-block border-mobile"
          ><em
            class="icon-block orange-color mr-2 fas fa-info-circle"
          />Certificación de Firma Electrónica:</span
        >
        <span class="col-12 font-weight-bold font-16 color-black">
          <div class="col-12 px-0">
            <validation-provider
              rules="required|markCheckbox"
              v-slot="{ errors }"
            >
              <check-box
                v-model="info.eSignatureCertification"
                :disabled="isDisabled"
                :hasError="Boolean(!hasCertifiedCheckbox && errors.length)"
                class="certification-checkbox"
                text="Certifico por este medio, que este referido podrá encontrarse en
                el expediente físico del estudiante firmado por todas las partes
                involucradas. (0502)"
              />
              <small
                v-if="!hasCertifiedCheckbox && errors.length"
                class="text-danger"
                >Favor certificar los términos y responsabilidades.</small
              >
            </validation-provider>
          </div>
        </span>
      </div>
    </validation-observer>
  </custom-callout>
</template>

<script>
import CustomCallout from "custom-callout";
import IconView from "icon-view";
import { CustomDropDown } from "@/components/common";
import CustomInput from "@/components/common/CustomInput";
import CheckBox from "CheckBox";
import ActionButton from "ActionButton";
import Topaz from "@quisit/topaz";
import mixAlert from "@/mixin/alert.mixin";
import mixWait from "@/mixin/loading.mixin";
import store from "@/store/index";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { SignatureTypes } from "@/utils/constants/signature/signatureTypes";
import { fileUtils } from "@/utils/fileUtil";

const SIGNATURE_TYPES = {
  SIGNATURE_PAD: 1,
  EMAIL: 2,
  MANUAL: 3
};

export default {
  name: "SignaturePanel",
  mixins: [mixAlert, mixWait],
  components: {
    CustomCallout,
    IconView,
    CustomDropDown,
    CustomInput,
    CheckBox,
    ActionButton,
    Topaz,
    ValidationObserver,
    ValidationProvider
  },
  props: {
    isPositionReadOnly: {
      type: Boolean,
      required: false,
      default: () => false
    },
    positions: {
      type: Array,
      required: true
    },
    defaultPositionValue: {
      type: Object,
      required: false,
      default: () => {}
    },
    isNameReadOnly: {
      type: Boolean,
      required: false,
      default: () => false
    },
    defaultNameValue: {
      type: String,
      required: false,
      default: () => ""
    },
    signatureTypes: {
      type: Array,
      required: true
    },
    isDisabled: {
      type: Boolean,
      default: () => false,
      required: false
    },
    studentSieId: {
      type: Number,
      default: () => 0
    },
    attendanceGroupId: {
      type: Number,
      default: () => 0
    }
  },
  data() {
    return {
      info: {
        name: "",
        email: "",
        selectedPosition: null,
        selectedSignatureType: null,
        eSignatureCertification: null
      },
      signatureModel: null,
      showGoSignButton: true
    };
  },
  computed: {
    showEmailInput() {
      return this.info.selectedSignatureType?.id == SIGNATURE_TYPES.EMAIL;
    },
    selectedStudent() {
      return store.getters.SelectedStudent;
    },
    hasCertifiedCheckbox() {
      return this.info?.eSignatureCertification;
    },
    isDigitalSignatureOptionSelected() {
      return (
        this.info?.selectedSignatureType?.id === SignatureTypes.signaturePad
      );
    },
    isReadOnlySignatureType() {
      return this.signatureTypes.length === 1;
    }
  },
  methods: {
    goSign() {
      this.$refs.topaz.startSigningInPad();
      const displayTime = 1000 * 60; // 60 seg

      setTimeout(() => {
        this.handleWaiting(false);
        this.$refs.topaz.clearInstance();
      }, displayTime);
    },
    resetSignature() {
      this.goSign();
    },
    handleWaiting(isSigning) {
      if (isSigning) this.ShowWait(null, true);
      else this.HideWait();
    },
    handleUserCancelled(err) {
      this.HideWait();
      this.$refs.topaz.clearInstance();

      // Handling error: Topaz SigPlusExtLite failed to start, another instance already running.
      if (err.toLowerCase().includes("already")) {
        this.ShowToast(
          "Alerta",
          "Cancele la firma en el pad digital e intente nuevamente.",
          "error"
        );
        return;
      }

      this.ShowToast(
        "Alerta",
        "El usuario ha cancelado la firma digital.",
        "error"
      );
    },
    saveSignature(base64) {
      this.showGoSignButton = false;
      this.signatureModel = base64;
    },
    async validateModel() {
      let isValid = await this.$refs.signatureForm.validate();

      if (!isValid) return false;

      if (
        this.info.selectedSignatureType.id === SignatureTypes.signaturePad &&
        !this.signatureModel
      ) {
        this.ShowToast("Alerta", "Es requerida la firma digital.", "error");
        return false;
      }
      return true;
    },
    async submitSignature() {
      const filename = `firma_estudiante_${this.selectedStudent?.studentId}.png`;
      const blobImage = fileUtils.base64toBlob(this.signatureModel, filename);
      let data = {
        file: blobImage,
        studentSieId: this.selectedStudent.studentId,
        tutorName: this.info.name,
        positionId: this.info.selectedPosition.id,
        signatureType: this.info.selectedSignatureType.id,
        attendanceGroupId: this.attendanceGroupId,
        email: this.info.email
      };

      return data;
    }
  },
  mounted() {
    this.info.selectedPosition = this.defaultPositionValue;
    this.info.name = this.defaultNameValue;
  },
  watch: {
    defaultPositionValue: {
      immediate: true,
      handler(newVal) {
        this.info.selectedPosition = newVal;
      }
    },
    "info.selectedSignatureType": {
      handler(value) {
        if (value?.id === SignatureTypes.signaturePad)
          this.showGoSignButton = true;
      }
    },
    signatureTypes: {
      handler(newVal) {
        if (newVal.length === 1) this.info.selectedSignatureType = newVal[0];
      }
    }
  }
};
</script>
<style lang="scss" scoped>
::v-deep .custom-input-signature div input:disabled {
  background: #f7f7f7;
  cursor: not-allowed;
}

:deep(.signature-container) {
  min-height: 12.5rem;
}

:deep(.img-signature) {
  max-height: 12.5rem;
}

.orange-color {
  color: #ff7058;
  background-color: #ffffff;
}

::v-deep .custom-icon {
  width: 30px;
  height: 30px;
  .dot {
    .title-icon {
      margin-top: 1px !important;
      font-size: 15px;
    }
  }
}

.certification-checkbox {
  font-weight: normal;
}

::v-deep .certification-checkbox label span {
  width: 26px !important;
  height: 26px !important;
}

::v-deep .certification-checkbox label span em {
  margin-left: -2px !important;
  width: 28px !important;
  height: 21px !important;
}

::v-deep input[type="checkbox"]:checked + label span {
  background-color: #2db782 !important;
}

::v-deep input[type="checkbox"]:checked + label span em {
  filter: brightness(0) invert(1);
}

::v-deep .certification-checkbox label div {
  font-size: 16px !important;
  font-weight: 700 !important;
}
</style>
